/* eslint-disable react/prop-types */
import React, { useState, useEffect, useRef } from "react";
import {
    Box,
    TextField,
    MenuItem,
    Typography,
    Alert,
    FormControlLabel,
    Checkbox,
    Button,
    List,
    ListItem,
    ListItemText,
    ListItemSecondaryAction,
    ListItemIcon,
    IconButton,
    Chip
} from "@mui/material";
import { CloudUpload, Delete, Visibility, FileCopy } from "@mui/icons-material";
import StandardFormDialog from "../../components/StandardFormDialog";
import api from "../../services/api";
import { formatarCPF, formatarCNPJ, formatarTelefone, formatarCEP } from "../../utils/formatadores";
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { ptBR } from 'date-fns/locale';
import { parseISO, format, isValid } from 'date-fns';

export default function CadastroRepresentante({ open, onClose, representanteId, onSuccess }) {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const fileInputRef = useRef(null);

    const [documentos, setDocumentos] = useState([]);

    const [dados, setDados] = useState({
        // 1. Identificação
        en_tipo_pessoa: "PF",
        st_nome: "",
        st_nome_fantasia: "",
        st_cpf_cnpj: "",
        st_rg_ie: "",
        st_inscricao_municipal: "",
        dt_nascimento_abertura: null,
        st_nacionalidade: "",
        en_estado_civil: "",

        // 2. Endereço
        st_cep: "",
        st_logradouro: "",
        st_numero: "",
        st_complemento: "",
        st_bairro: "",
        st_cidade: "",
        st_estado: "",
        st_pais: "Brasil",

        // 3. Contatos
        st_telefone: "",
        st_whatsapp: "",
        st_email: "",
        st_email_financeiro: "",
        st_nome_contato: "",

        // 4. Dados Bancários
        st_banco: "",
        st_agencia: "",
        st_conta: "",
        en_tipo_conta: "Corrente",
        st_chave_pix: "",
        st_titular_conta: "",
        st_cpf_cnpj_conta: "",

        // 5. Informações Comerciais
        st_codigo: "",
        st_regiao: "",
        st_produtos: "",
        nu_comissao: "",
        en_modelo_comissao: "",
        st_forma_apuracao: "",
        en_periodicidade: "",

        // 6. Informações Contratuais
        dt_inicio_contrato: null,
        dt_fim_contrato: null,
        bo_exclusivo: false,
        en_status: "ativo",
        bo_aceite_contrato: false,

        // 8. Informações Fiscais
        en_regime_tributario: "",
        st_cnae: "",
        nu_retencao: "",
        bo_emite_nf: false,
        st_municipio_iss: "",

        // 9. Controle
        tx_observacoes: ""
    });

    useEffect(() => {
        if (representanteId) {
            carregarRepresentante();
        }
    }, [representanteId]);

    const carregarRepresentante = async () => {
        setLoading(true);
        try {
            const res = await api.get(`/representante/${representanteId}`);
            if (res.data.status === 'sucesso') {
                const r = res.data.dados;
                setDados({
                    ...r,
                    dt_nascimento_abertura: r.dt_nascimento_abertura,
                    dt_inicio_contrato: r.dt_inicio_contrato,
                    dt_fim_contrato: r.dt_fim_contrato
                });
                if (r.documentos) {
                    setDocumentos(r.documentos);
                }
            }
        } catch (err) {
            console.error(err);
            setError("Erro ao carregar dados do representante.");
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setDados(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleDateChange = (name, newValue) => {
        const dateStr = (newValue && isValid(newValue)) ? format(newValue, 'yyyy-MM-dd') : null;
        setDados(prev => ({ ...prev, [name]: dateStr }));
    };

    const handleBuscaCEP = async () => {
        if (dados.st_cep && dados.st_cep.length >= 8) {
            try {
                const cepLimpo = dados.st_cep.replace(/\D/g, '');
                if (cepLimpo.length === 8) {
                    const res = await api.get(`https://viacep.com.br/ws/${cepLimpo}/json/`);
                    if (!res.data.erro) {
                        setDados(prev => ({
                            ...prev,
                            st_logradouro: res.data.logradouro,
                            st_bairro: res.data.bairro,
                            st_cidade: res.data.localidade,
                            st_estado: res.data.uf
                        }));
                    }
                }
            } catch (err) {
                console.error("Erro ao buscar CEP", err);
            }
        }
    };

    const handleFileUpload = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        // Limite de 5MB
        if (file.size > 5 * 1024 * 1024) {
            setError("O arquivo deve ter no máximo 5MB.");
            return;
        }

        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => {
            const base64 = reader.result;
            const novoDoc = {
                st_nome_arquivo: file.name,
                st_tipo_arquivo: file.type,
                st_categoria: "Outros", // Poderia ter um select para categoria antes
                tx_conteudo_base64: base64,
                novo: true
            };
            setDocumentos(prev => [...prev, novoDoc]);
        };
        reader.onerror = (error) => {
            console.error("Error converting file to base64:", error);
            setError("Erro ao processar arquivo.");
        };

        // Reset input value to allow selecting same file again
        event.target.value = '';
    };

    const handleRemoveDocument = async (index) => {
        const doc = documentos[index];
        if (doc.id_documento) {
            // Documento já salvo no banco
            if (window.confirm("Deseja realmente excluir este documento permanentemente?")) {
                try {
                    await api.delete(`/representante/documento/${doc.id_documento}`);
                    setDocumentos(prev => prev.filter((_, i) => i !== index));
                } catch (err) {
                    console.error(err);
                    setError("Erro ao excluir documento.");
                }
            }
        } else {
            // Documento novo (apenas removido da lista local)
            setDocumentos(prev => prev.filter((_, i) => i !== index));
        }
    };

    const handleDownloadDocument = (doc) => {
        if (!doc.tx_conteudo_base64) {
            // Se o documento existe no banco mas o base64 não foi carregado na lista (otimização), teria que buscar.
            // Mas no meu controller eu incluí o base64, então deve estar aqui.
            alert("Conteúdo do arquivo não disponível.");
            return;
        }
        const link = document.createElement("a");
        link.href = doc.tx_conteudo_base64;
        link.download = doc.st_nome_arquivo;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };


    const salvar = async () => {
        setLoading(true);
        setError(null);
        try {
            // Filtra documentos novos para enviar no payload
            // Documentos existentes não precisam ser reenviados a menos que tenhamos edição de metadados, o que não temos aqui.
            // O controller espera 'documentos' para criar novos.
            const documentosNovos = documentos.filter(d => d.novo);

            const payload = {
                ...dados,
                st_cpf_cnpj: dados.st_cpf_cnpj.replace(/\D/g, ''),
                st_cep: dados.st_cep.replace(/\D/g, ''),
                st_telefone: dados.st_telefone.replace(/\D/g, ''),
                st_whatsapp: dados.st_whatsapp?.replace(/\D/g, '') || '',
                documentos: documentosNovos,
                id_representante: undefined,
                createdAt: undefined,
                updatedAt: undefined
            };

            if (representanteId) {
                await api.put(`/representante/${representanteId}`, payload);
                setSuccess("Representante atualizado com sucesso!");
                // Recarregar para atualizar lista de documentos (getID dos novos)
                if (documentosNovos.length > 0) setTimeout(() => carregarRepresentante(), 500);
            } else {
                await api.post('/representante', payload);
                setSuccess("Representante criado com sucesso!");
            }

            setTimeout(() => {
                onSuccess("Operação realizada com sucesso");
            }, 1000);

        } catch (err) {
            setError(err.response?.data?.mensagem || err.message || "Erro ao salvar.");
            setLoading(false);
        }
    };

    // --- TABS (mantidas iguais, apenas documentação atualizada) ---

    const tabIdentificacao = (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField select label="Tipo de Representante" name="en_tipo_pessoa" value={dados.en_tipo_pessoa} onChange={handleChange} sx={{ width: 200 }}>
                    <MenuItem value="PF">Pessoa Física</MenuItem>
                    <MenuItem value="PJ">Pessoa Jurídica</MenuItem>
                </TextField>
                <TextField
                    fullWidth
                    label={dados.en_tipo_pessoa === 'PF' ? "CPF" : "CNPJ"}
                    name="st_cpf_cnpj"
                    value={dados.en_tipo_pessoa === 'PF' ? formatarCPF(dados.st_cpf_cnpj) : formatarCNPJ(dados.st_cpf_cnpj)}
                    onChange={e => handleChange({ target: { name: 'st_cpf_cnpj', value: e.target.value.replace(/\D/g, '') } })}
                    required
                />
            </Box>

            <TextField fullWidth label={dados.en_tipo_pessoa === 'PF' ? "Nome Completo" : "Razão Social"} name="st_nome" value={dados.st_nome} onChange={handleChange} required />

            {dados.en_tipo_pessoa === 'PJ' && (
                <TextField fullWidth label="Nome Fantasia" name="st_nome_fantasia" value={dados.st_nome_fantasia} onChange={handleChange} />
            )}

            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <TextField
                    label={dados.en_tipo_pessoa === 'PF' ? "RG" : "Inscrição Estadual"}
                    name="st_rg_ie"
                    value={dados.st_rg_ie}
                    onChange={handleChange}
                    sx={{ flex: 1 }}
                />
                <TextField
                    label="Inscrição Municipal"
                    name="st_inscricao_municipal"
                    value={dados.st_inscricao_municipal}
                    onChange={handleChange}
                    sx={{ flex: 1 }}
                />
            </Box>

            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <DatePicker
                    label={dados.en_tipo_pessoa === 'PF' ? "Data de Nascimento" : "Data de Abertura"}
                    value={dados.dt_nascimento_abertura ? parseISO(dados.dt_nascimento_abertura) : null}
                    onChange={(val) => handleDateChange('dt_nascimento_abertura', val)}
                    slotProps={{ textField: { fullWidth: true, sx: { flex: 1 } } }}
                />
                <TextField label="Nacionalidade" name="st_nacionalidade" value={dados.st_nacionalidade} onChange={handleChange} sx={{ flex: 1 }} />
                {dados.en_tipo_pessoa === 'PF' && (
                    <TextField label="Estado Civil" name="en_estado_civil" value={dados.en_estado_civil} onChange={handleChange} sx={{ flex: 1 }} />
                )}
            </Box>
        </Box>
    );

    const tabEndereco = (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField label="CEP" name="st_cep" value={formatarCEP(dados.st_cep)} onChange={e => handleChange({ target: { name: 'st_cep', value: e.target.value.replace(/\D/g, '') } })} onBlur={handleBuscaCEP} sx={{ width: 150 }} required />
                <TextField fullWidth label="Logradouro" name="st_logradouro" value={dados.st_logradouro} onChange={handleChange} required />
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField label="Número" name="st_numero" value={dados.st_numero} onChange={handleChange} sx={{ width: 100 }} required />
                <TextField fullWidth label="Complemento" name="st_complemento" value={dados.st_complemento} onChange={handleChange} />
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField fullWidth label="Bairro" name="st_bairro" value={dados.st_bairro} onChange={handleChange} required />
                <TextField fullWidth label="Cidade" name="st_cidade" value={dados.st_cidade} onChange={handleChange} required />
                <TextField label="UF" name="st_estado" value={dados.st_estado} onChange={handleChange} sx={{ width: 80 }} required />
            </Box>
            <TextField fullWidth label="País" name="st_pais" value={dados.st_pais} onChange={handleChange} />
        </Box>
    );

    const tabContatos = (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField fullWidth label="Telefone Principal" name="st_telefone" value={formatarTelefone(dados.st_telefone)} onChange={e => handleChange({ target: { name: 'st_telefone', value: e.target.value.replace(/\D/g, '') } })} />
                <TextField fullWidth label="WhatsApp" name="st_whatsapp" value={formatarTelefone(dados.st_whatsapp)} onChange={e => handleChange({ target: { name: 'st_whatsapp', value: e.target.value.replace(/\D/g, '') } })} />
            </Box>
            <TextField fullWidth label="E-mail Principal" name="st_email" type="email" value={dados.st_email} onChange={handleChange} />
            <TextField fullWidth label="E-mail Financeiro" name="st_email_financeiro" type="email" value={dados.st_email_financeiro} onChange={handleChange} />
            {dados.en_tipo_pessoa === 'PJ' && (
                <TextField fullWidth label="Nome Responsável" name="st_nome_contato" value={dados.st_nome_contato} onChange={handleChange} />
            )}
        </Box>
    );

    const tabBancarios = (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField fullWidth label="Banco" name="st_banco" value={dados.st_banco} onChange={handleChange} />
                <TextField label="Agência" name="st_agencia" value={dados.st_agencia} onChange={handleChange} />
                <TextField label="Conta" name="st_conta" value={dados.st_conta} onChange={handleChange} />
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField select label="Tipo de Conta" name="en_tipo_conta" value={dados.en_tipo_conta} onChange={handleChange} sx={{ width: 200 }}>
                    <MenuItem value="Corrente">Corrente</MenuItem>
                    <MenuItem value="Poupanca">Poupança</MenuItem>
                </TextField>
                <TextField fullWidth label="Chave PIX" name="st_chave_pix" value={dados.st_chave_pix} onChange={handleChange} />
            </Box>
            <TextField fullWidth label="Titular da Conta" name="st_titular_conta" value={dados.st_titular_conta} onChange={handleChange} />
            <TextField fullWidth label="CPF/CNPJ Titular" name="st_cpf_cnpj_conta" value={dados.st_cpf_cnpj_conta} onChange={handleChange} />
        </Box>
    );

    const tabComerciais = (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField label="Código" name="st_codigo" value={dados.st_codigo} onChange={handleChange} sx={{ width: 150 }} />
                <TextField fullWidth label="Região de Atuação" name="st_regiao" value={dados.st_regiao} onChange={handleChange} />
            </Box>
            <TextField fullWidth label="Produtos Habilitados" name="st_produtos" value={dados.st_produtos} onChange={handleChange} helperText="Ex: Alimentação, Refeição..." />
            <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField label="% Comissão" name="nu_comissao" value={dados.nu_comissao} onChange={handleChange} type="number" />
                <TextField fullWidth label="Modelo Comissão" name="en_modelo_comissao" value={dados.en_modelo_comissao} onChange={handleChange} placeholder="Recorrente, Ativação..." />
            </Box>
            <TextField fullWidth label="Forma de Apuração" name="st_forma_apuracao" value={dados.st_forma_apuracao} onChange={handleChange} multiline rows={2} />
            <TextField fullWidth label="Periodicidade Pagamento" name="en_periodicidade" value={dados.en_periodicidade} onChange={handleChange} />
        </Box>
    );

    const tabContratuais = (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Box sx={{ display: 'flex', gap: 2 }}>
                <DatePicker
                    label="Início do Contrato"
                    value={dados.dt_inicio_contrato ? parseISO(dados.dt_inicio_contrato) : null}
                    onChange={(val) => handleDateChange('dt_inicio_contrato', val)}
                    slotProps={{ textField: { fullWidth: true } }}
                />
                <DatePicker
                    label="Fim do Contrato"
                    value={dados.dt_fim_contrato ? parseISO(dados.dt_fim_contrato) : null}
                    onChange={(val) => handleDateChange('dt_fim_contrato', val)}
                    slotProps={{ textField: { fullWidth: true } }}
                />
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField select label="Status" name="en_status" value={dados.en_status} onChange={handleChange} fullWidth>
                    <MenuItem value="ativo">Ativo</MenuItem>
                    <MenuItem value="suspenso">Suspenso</MenuItem>
                    <MenuItem value="inativo">Inativo</MenuItem>
                </TextField>
                <TextField select label="Tipo Contrato" name="bo_exclusivo" value={dados.bo_exclusivo ? 'sim' : 'nao'} onChange={e => handleChange({ target: { name: 'bo_exclusivo', value: e.target.value === 'sim', type: 'select' } })} fullWidth>
                    <MenuItem value="sim">Exclusivo</MenuItem>
                    <MenuItem value="nao">Não Exclusivo</MenuItem>
                </TextField>
            </Box>
            <FormControlLabel control={<Checkbox checked={dados.bo_aceite_contrato} onChange={handleChange} name="bo_aceite_contrato" />} label="Contrato Aceito/Assinado" />
        </Box>
    );

    const tabDocumentacao = (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <Button
                    variant="contained"
                    component="label"
                    startIcon={<CloudUpload />}
                    sx={{ backgroundColor: '#20ac9c', '&:hover': { backgroundColor: '#167a6f' } }}
                >
                    Adicionar Documento
                    <input
                        type="file"
                        hidden
                        ref={fileInputRef}
                        onChange={handleFileUpload}
                    />
                </Button>
                <Typography variant="caption" color="textSecondary">Max: 5MB. Formatos: PDF, Imagens.</Typography>
            </Box>

            <List sx={{ width: '100%', bgcolor: 'background.paper', border: '1px solid #ddd', borderRadius: 1 }}>
                {documentos.length === 0 && (
                    <ListItem>
                        <ListItemText primary="Nenhum documento anexado." sx={{ color: 'text.secondary', textAlign: 'center' }} />
                    </ListItem>
                )}
                {documentos.map((doc, index) => (
                    <ListItem key={index} divider>
                        <ListItemIcon>
                            <FileCopy color="primary" />
                        </ListItemIcon>
                        <ListItemText
                            primary={doc.st_nome_arquivo}
                            secondary={doc.novo ? "Novo (Não salvo)" : `Cadastrado em: ${doc.createdAt ? new Date(doc.createdAt).toLocaleDateString() : '-'}`}
                        />
                        <ListItemSecondaryAction>
                            <IconButton edge="end" aria-label="download" onClick={() => handleDownloadDocument(doc)} sx={{ mr: 1 }}>
                                <Visibility />
                            </IconButton>
                            <IconButton edge="end" aria-label="delete" onClick={() => handleRemoveDocument(index)} color="error">
                                <Delete />
                            </IconButton>
                        </ListItemSecondaryAction>
                    </ListItem>
                ))}
            </List>

            <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2">Checklist Obrigatório:</Typography>
                <FormControlLabel control={<Checkbox />} label="Documento de Identificação (RG/CNH)" />
                <FormControlLabel control={<Checkbox />} label="CPF ou CNPJ" />
                <FormControlLabel control={<Checkbox />} label="Comprovante de Endereço" />
            </Box>
        </Box>
    );

    const tabFiscais = (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <TextField fullWidth label="Regime Tributário" name="en_regime_tributario" value={dados.en_regime_tributario} onChange={handleChange} />
            <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField fullWidth label="CNAE Principal" name="st_cnae" value={dados.st_cnae} onChange={handleChange} />
                <TextField label="% Retenção" name="nu_retencao" value={dados.nu_retencao} onChange={handleChange} type="number" />
            </Box>
            <FormControlLabel control={<Checkbox checked={dados.bo_emite_nf} onChange={handleChange} name="bo_emite_nf" />} label="Emite Nota Fiscal?" />
            <TextField fullWidth label="Município de Recolhimento ISS" name="st_municipio_iss" value={dados.st_municipio_iss} onChange={handleChange} />
        </Box>
    );

    const tabControle = (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {representanteId && (
                <TextField fullWidth label="Atualizado Em" value={dados.updatedAt ? new Date(dados.updatedAt).toLocaleString() : ''} disabled />
            )}
            <TextField fullWidth label="Observações Internas" name="tx_observacoes" multiline rows={4} value={dados.tx_observacoes} onChange={handleChange} />
        </Box>
    );

    return (
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ptBR}>
            <StandardFormDialog
                open={open}
                onClose={onClose}
                title={representanteId ? "Editar Representante" : "Novo Representante"}
                tabs={[
                    { label: "Identificação", content: tabIdentificacao },
                    { label: "Endereço", content: tabEndereco },
                    { label: "Contatos", content: tabContatos },
                    { label: "Bancários", content: tabBancarios },
                    { label: "Comercial", content: tabComerciais },
                    { label: "Contratual", content: tabContratuais },
                    { label: "Fiscais", content: tabFiscais },
                    { label: "Documentação", content: tabDocumentacao },
                    { label: "Controle", content: tabControle },
                ]}
                onSave={salvar}
                loading={loading}
                error={error}
                success={success}
                maxWidth="lg"
            />
        </LocalizationProvider>
    );
}
